import { frontendURL } from '../../../../helper/URLHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import store from 'dashboard/store';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import { getDefaultReportsRouteName } from 'dashboard/helper/routeHelpers';
import {
  REPORTS_PERMISSIONS,
  REPORT_OVERVIEW_PERMISSIONS,
  REPORT_CONVERSATION_PERMISSIONS,
  REPORT_AGENT_PERMISSIONS,
  REPORT_CASE_CLOSING_TYPES_PERMISSIONS,
  REPORT_CHAT_HISTORY_EXPORT_PERMISSIONS,
  REPORT_LABEL_PERMISSIONS,
  REPORT_INBOX_PERMISSIONS,
  REPORT_TEAM_PERMISSIONS,
  REPORT_VIP_PERMISSIONS,
  REPORT_VIP_AGENT_PERMISSIONS,
  REPORT_ISSUE_PERMISSIONS,
  REPORT_TOKEN_USAGE_PERMISSIONS,
  REPORT_CSAT_PERMISSIONS,
  REPORT_SLA_PERMISSIONS,
  REPORT_BOT_PERMISSIONS,
  REPORT_TELEGRAM_BOT_PERMISSIONS,
  REPORTS_OVERRIDE_PERMISSIONS,
  LINE_DELIVERY_REPORTS_PERMISSIONS,
  CROSS_BRAND_REPORTS_PERMISSIONS,
} from 'dashboard/constants/permissions';

const ReportsWrapper = () => import('./components/ReportsWrapper.vue');
const Index = () => import('./Index.vue');
const AgentReportsIndex = () => import('./AgentReportsIndex.vue');
const InboxReportsIndex = () => import('./InboxReportsIndex.vue');
const TeamReportsIndex = () => import('./TeamReportsIndex.vue');
const LabelReportsIndex = () => import('./LabelReportsIndex.vue');
const VipReportsIndex = () => import('./VipReportsIndex.vue');
const VipAgentReportsIndex = () => import('./VipAgentReportsIndex.vue');
const IssueReportsIndex = () => import('./IssueReportsIndex.vue');
const QAOverridesIndex = () => import('./QAOverridesIndex.vue');
const CrossBrandReportsIndex = () => import('./CrossBrandReportsIndex.vue');
const AgentReportsShow = () => import('./AgentReportsShow.vue');
const InboxReportsShow = () => import('./InboxReportsShow.vue');
const TeamReportsShow = () => import('./TeamReportsShow.vue');
const LabelReportsShow = () => import('./LabelReportsShow.vue');
const VipReportsShow = () => import('./VipReportsShow.vue');
const AgentReports = () => import('./AgentReports.vue');
const InboxReports = () => import('./InboxReports.vue');
const LabelReports = () => import('./LabelReports.vue');
const TeamReports = () => import('./TeamReports.vue');
const VipReports = () => import('./VipReports.vue');
const CsatResponses = () => import('./CsatResponses.vue');
const BotReports = () => import('./BotReports.vue');
const TelegramBotReports = () => import('./TelegramBotReports.vue');
const ChatHistoryExportReports = () => import('./ChatHistoryExportReports.vue');
const LiveChatFormsExportReports = () =>
  import('./LiveChatFormsExportReports.vue');
const CaseClosingTypesReports = () => import('./CaseClosingTypesReports.vue');
const LiveReports = () => import('./LiveReports.vue');
const Last7DaysReports = () => import('./Last7DaysReports.vue');
const TotalChatsReports = () => import('./TotalChatsReports.vue');
const ChatEngagementReports = () => import('./ChatEngagementReports.vue');
const MissedChatsReports = () => import('./MissedChatsReports.vue');
const CampaignsConversionReports = () =>
  import('./CampaignsConversionReports.vue');
const TagsUsageReports = () => import('./TagsUsageReports.vue');
const ChatAvailabilityReports = () => import('./ChatAvailabilityReports.vue');
const ChatDurationReports = () => import('./ChatDurationReports.vue');
const ChatResponseTimesReports = () => import('./ChatResponseTimesReports.vue');
const StaffingPredictionReports = () =>
  import('./StaffingPredictionReports.vue');
const AgentActivityReports = () => import('./AgentActivityReports.vue');
const AgentChatStatusReports = () => import('./AgentChatStatusReports.vue');
const QueuedCustomersReports = () => import('./QueuedCustomersReports.vue');
const TopCustomerQuestionsReports = () =>
  import('./TopCustomerQuestionsReports.vue');
const LineReportsIndex = () => import('./LineReportsIndex.vue');
const SLAReports = () => import('./SLAReports.vue');
const TokenUsageReports = () => import('./TokenUsageReports.vue');
const REPORTS_NON_LINE_VIEW_PERMISSIONS = [
  REPORT_OVERVIEW_PERMISSIONS,
  REPORT_CONVERSATION_PERMISSIONS,
  REPORT_AGENT_PERMISSIONS,
  REPORT_CASE_CLOSING_TYPES_PERMISSIONS,
  REPORT_CHAT_HISTORY_EXPORT_PERMISSIONS,
  REPORT_LABEL_PERMISSIONS,
  REPORT_INBOX_PERMISSIONS,
  REPORT_TEAM_PERMISSIONS,
  REPORT_VIP_PERMISSIONS,
  REPORT_VIP_AGENT_PERMISSIONS,
  REPORT_ISSUE_PERMISSIONS,
  REPORT_TOKEN_USAGE_PERMISSIONS,
  REPORT_CSAT_PERMISSIONS,
  REPORT_SLA_PERMISSIONS,
  REPORT_BOT_PERMISSIONS,
  REPORT_TELEGRAM_BOT_PERMISSIONS,
  REPORTS_OVERRIDE_PERMISSIONS,
  CROSS_BRAND_REPORTS_PERMISSIONS,
].filter(Boolean);

const reportMeta = (...permissions) => ({
  featureFlag: FEATURE_FLAGS.REPORTS,
  permissions: {
    anyOf: [
      'administrator',
      REPORTS_PERMISSIONS,
      ...REPORTS_NON_LINE_VIEW_PERMISSIONS,
      ...permissions.filter(Boolean),
    ],
  },
});

const strictReportMeta = permission => ({
  featureFlag: FEATURE_FLAGS.REPORTS,
  permissions: ['administrator', REPORTS_PERMISSIONS, permission].filter(
    Boolean
  ),
});

const lineReportMeta = () => ({
  featureFlag: FEATURE_FLAGS.REPORTS,
  permissions: [
    'administrator',
    REPORTS_PERMISSIONS,
    LINE_DELIVERY_REPORTS_PERMISSIONS,
  ],
});

const defaultReportRouteNameFor = to => {
  const currentUser = store.getters.getCurrentUser;
  const permissions = getUserPermissions(
    currentUser,
    Number(to.params.accountId)
  );
  return (
    getDefaultReportsRouteName(permissions) || 'account_last_7_days_reports'
  );
};

const oldReportRoutes = [
  {
    path: 'agent',
    name: 'agent_reports',
    meta: strictReportMeta(REPORT_AGENT_PERMISSIONS),
    component: AgentReports,
  },
  {
    path: 'inboxes',
    name: 'inbox_reports',
    meta: reportMeta(REPORT_INBOX_PERMISSIONS),
    component: InboxReports,
  },
  {
    path: 'label',
    name: 'label_reports',
    meta: reportMeta(REPORT_LABEL_PERMISSIONS),
    component: LabelReports,
  },
  {
    path: 'teams',
    name: 'team_reports',
    meta: reportMeta(REPORT_TEAM_PERMISSIONS),
    component: TeamReports,
  },
  {
    path: 'vip',
    name: 'vip_reports',
    meta: reportMeta(REPORT_VIP_PERMISSIONS),
    component: VipReports,
  },
];

const revisedReportRoutes = [
  {
    path: 'agents_overview',
    name: 'agent_reports_index',
    meta: strictReportMeta(REPORT_AGENT_PERMISSIONS),
    component: AgentReportsIndex,
  },
  {
    path: 'agents/:id',
    name: 'agent_reports_show',
    meta: strictReportMeta(REPORT_AGENT_PERMISSIONS),
    component: AgentReportsShow,
  },

  {
    path: 'inboxes_overview',
    name: 'inbox_reports_index',
    meta: reportMeta(REPORT_INBOX_PERMISSIONS),
    component: InboxReportsIndex,
  },
  {
    path: 'inboxes/:id',
    name: 'inbox_reports_show',
    meta: reportMeta(REPORT_INBOX_PERMISSIONS),
    component: InboxReportsShow,
  },
  {
    path: 'teams_overview',
    name: 'team_reports_index',
    meta: reportMeta(REPORT_TEAM_PERMISSIONS),
    component: TeamReportsIndex,
  },
  {
    path: 'teams/:id',
    name: 'team_reports_show',
    meta: reportMeta(REPORT_TEAM_PERMISSIONS),
    component: TeamReportsShow,
  },
  {
    path: 'vip_overview',
    name: 'vip_reports_index',
    meta: reportMeta(REPORT_VIP_PERMISSIONS),
    component: VipReportsIndex,
  },
  {
    path: 'vip_agents_overview',
    name: 'vip_agent_reports_index',
    meta: reportMeta(REPORT_VIP_AGENT_PERMISSIONS),
    component: VipAgentReportsIndex,
  },
  {
    path: 'issues_overview',
    name: 'issue_reports_index',
    meta: reportMeta(REPORT_ISSUE_PERMISSIONS),
    component: IssueReportsIndex,
  },
  {
    path: 'qa_overrides',
    name: 'qa_overrides_reports_index',
    meta: reportMeta(REPORTS_OVERRIDE_PERMISSIONS),
    component: QAOverridesIndex,
  },
  {
    path: 'vip/:id',
    name: 'vip_reports_show',
    meta: reportMeta(REPORT_VIP_PERMISSIONS),
    component: VipReportsShow,
  },
  {
    path: 'labels_overview',
    name: 'label_reports_index',
    meta: reportMeta(REPORT_LABEL_PERMISSIONS),
    component: LabelReportsIndex,
  },
  {
    path: 'labels/:id',
    name: 'label_reports_show',
    meta: reportMeta(REPORT_LABEL_PERMISSIONS),
    component: LabelReportsShow,
  },
  {
    path: 'all_brands',
    name: 'cross_brand_reports_index',
    meta: reportMeta(CROSS_BRAND_REPORTS_PERMISSIONS),
    component: CrossBrandReportsIndex,
  },
  {
    path: 'token_usage',
    name: 'token_usage_reports',
    meta: reportMeta(REPORT_TOKEN_USAGE_PERMISSIONS),
    component: TokenUsageReports,
  },
  {
    path: 'live_chat_forms_export',
    name: 'live_chat_forms_export_reports',
    meta: reportMeta(REPORT_CHAT_HISTORY_EXPORT_PERMISSIONS),
    component: LiveChatFormsExportReports,
  },
  {
    path: 'case_closing_types',
    name: 'case_closing_types_reports',
    meta: reportMeta(REPORT_CASE_CLOSING_TYPES_PERMISSIONS),
    component: CaseClosingTypesReports,
  },
  {
    path: 'chat_history_export',
    name: 'chat_history_export_reports',
    meta: reportMeta(REPORT_CHAT_HISTORY_EXPORT_PERMISSIONS),
    component: ChatHistoryExportReports,
  },
];

export default {
  routes: [
    {
      path: frontendURL('accounts/:accountId/reports'),
      component: ReportsWrapper,
      children: [
        {
          path: '',
          redirect: to => {
            return {
              name: defaultReportRouteNameFor(to),
              params: to.params,
            };
          },
        },
        {
          path: 'overview',
          name: 'account_overview_reports',
          meta: reportMeta(REPORT_OVERVIEW_PERMISSIONS),
          component: LiveReports,
        },
        {
          path: 'last_7_days',
          name: 'account_last_7_days_reports',
          meta: reportMeta(REPORT_OVERVIEW_PERMISSIONS),
          component: Last7DaysReports,
        },
        {
          path: 'total_chats',
          name: 'total_chats_reports',
          meta: reportMeta(REPORT_CONVERSATION_PERMISSIONS),
          component: TotalChatsReports,
        },
        {
          path: 'chat_engagement',
          name: 'chat_engagement_reports',
          meta: reportMeta(REPORT_CONVERSATION_PERMISSIONS),
          component: ChatEngagementReports,
        },
        {
          path: 'missed_chats',
          name: 'missed_chats_reports',
          meta: reportMeta(REPORT_CONVERSATION_PERMISSIONS),
          component: MissedChatsReports,
        },
        {
          path: 'campaigns_conversion',
          name: 'campaigns_conversion_reports',
          meta: {
            featureFlag: FEATURE_FLAGS.REPORTS,
            permissions: [
              'administrator',
              REPORTS_PERMISSIONS,
              REPORT_CONVERSATION_PERMISSIONS,
            ],
          },
          component: CampaignsConversionReports,
        },
        {
          path: 'tags_usage',
          name: 'tags_usage_reports',
          meta: {
            featureFlag: FEATURE_FLAGS.REPORTS,
            permissions: [
              'administrator',
              REPORTS_PERMISSIONS,
              REPORT_CONVERSATION_PERMISSIONS,
            ],
          },
          component: TagsUsageReports,
        },
        {
          path: 'conversation',
          name: 'conversation_reports',
          meta: reportMeta(REPORT_CONVERSATION_PERMISSIONS),
          component: Index,
        },
        ...oldReportRoutes,
        ...revisedReportRoutes,
        {
          path: 'sla',
          name: 'sla_reports',
          meta: {
            featureFlag: FEATURE_FLAGS.REPORTS,
            permissions: [
              'administrator',
              REPORTS_PERMISSIONS,
              REPORT_SLA_PERMISSIONS,
            ],
          },
          component: SLAReports,
        },
        {
          path: 'csat',
          name: 'csat_reports',
          meta: {
            featureFlag: FEATURE_FLAGS.REPORTS,
            permissions: [
              'administrator',
              REPORTS_PERMISSIONS,
              REPORT_CSAT_PERMISSIONS,
            ],
          },
          component: CsatResponses,
        },
        {
          path: 'chat_availability',
          name: 'chat_availability_reports',
          meta: {
            featureFlag: FEATURE_FLAGS.REPORTS,
            permissions: [
              'administrator',
              REPORTS_PERMISSIONS,
              REPORT_CONVERSATION_PERMISSIONS,
            ],
          },
          component: ChatAvailabilityReports,
        },
        {
          path: 'chat_duration',
          name: 'chat_duration_reports',
          meta: {
            featureFlag: FEATURE_FLAGS.REPORTS,
            permissions: [
              'administrator',
              REPORTS_PERMISSIONS,
              REPORT_CONVERSATION_PERMISSIONS,
            ],
          },
          component: ChatDurationReports,
        },
        {
          path: 'chat_response_times',
          name: 'chat_response_times_reports',
          meta: {
            featureFlag: FEATURE_FLAGS.REPORTS,
            permissions: [
              'administrator',
              REPORTS_PERMISSIONS,
              REPORT_AGENT_PERMISSIONS,
            ],
          },
          component: ChatResponseTimesReports,
        },
        {
          path: 'staffing_prediction',
          name: 'staffing_prediction_reports',
          meta: reportMeta(REPORT_AGENT_PERMISSIONS),
          component: StaffingPredictionReports,
        },
        {
          path: 'agent_activity',
          name: 'agent_activity_reports',
          meta: reportMeta(REPORT_AGENT_PERMISSIONS),
          component: AgentActivityReports,
        },
        {
          path: 'agent_chat_status',
          name: 'agent_chat_status_reports',
          meta: reportMeta(REPORT_AGENT_PERMISSIONS),
          component: AgentChatStatusReports,
        },
        {
          path: 'queued_customers',
          name: 'queued_customers_reports',
          meta: reportMeta(REPORT_CONVERSATION_PERMISSIONS),
          component: QueuedCustomersReports,
        },
        {
          path: 'top_customer_questions',
          name: 'top_customer_questions_reports',
          meta: {
            featureFlag: FEATURE_FLAGS.REPORTS,
            permissions: [
              'administrator',
              REPORTS_PERMISSIONS,
              REPORT_CONVERSATION_PERMISSIONS,
            ],
          },
          component: TopCustomerQuestionsReports,
        },
        {
          path: 'bot',
          name: 'bot_reports',
          meta: reportMeta(REPORT_BOT_PERMISSIONS),
          component: BotReports,
        },
        {
          path: 'telegram_bot',
          name: 'telegram_bot_reports',
          meta: reportMeta(REPORT_TELEGRAM_BOT_PERMISSIONS),
          component: TelegramBotReports,
        },
        {
          path: 'line_delivery',
          name: 'line_delivery_reports',
          meta: lineReportMeta(),
          component: LineReportsIndex,
        },
      ],
    },
  ],
};
